import { createContext, useEffect, useMemo, useState } from "react";

export { COMMENT_SIDEBAR_WIDTH } from "../commentSidebarWidth";

export const PADDING_AND_BUTTON_WIDTH = 100;
export const HEIGHT_PADDING = 8;
const DOCUMENT_PADDING = 40; // the document has 40px of padding to the left and right
const BUTTON_WIDTH = 40;

let canvas = document.createElement("canvas");

const getTextMetrics = (titleEditorRef, title) => {
  if (!titleEditorRef.current) return;

  const ctx = canvas.getContext("2d");
  ctx.font = getComputedStyle(titleEditorRef.current).font || "16px sans-serif";

  return ctx.measureText(title);
};

export const getTitleMaxWidth = (titleEditorRef, widthOffset = 0) => {
  const parentDocumentElem = titleEditorRef.current?.closest("#editor-section");
  if (!parentDocumentElem) return;

  return Math.max(50, parentDocumentElem.clientWidth - PADDING_AND_BUTTON_WIDTH - widthOffset);
};

const getTextHeight = (titleEditorRef, title, maxWidth) => {
  if (!titleEditorRef.current || !title || !maxWidth) {
    return;
  }

  const textMetrics = getTextMetrics(titleEditorRef, title);
  const singleRowHeight = textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent + HEIGHT_PADDING;

  // Calculate total height of all rows
  const hasNewline = typeof title === "string" && title.includes("\n");
  const isSingleLine = !hasNewline && textMetrics.width < maxWidth;
  const h = isSingleLine ? singleRowHeight : titleEditorRef.current.scrollHeight; // Multiple lines

  // Calculate the offset needed to center the first row
  // This is half of the additional height from the wrapped rows
  const offset = isSingleLine ? 0 : -(h - singleRowHeight) / 2;

  // Offset can be NaN on initial load
  return { offset: offset || 0, glyphHeight: h, isSingleLine };
};

const determineMenuPosition = (titleEditorRef, maxTitleWidth) => {
  if (!titleEditorRef.current) {
    return;
  }

  const parentDocumentElem = titleEditorRef.current.closest("#editor-section");
  const menuBox = parentDocumentElem.querySelector("#document-linking-menu");
  if (!menuBox) {
    return;
  }

  const menuButton = menuBox.previousElementSibling || undefined;
  const menuButtonBoundingBox = menuButton.getBoundingClientRect();
  const parentDocumentElemBoundingBox = parentDocumentElem.getBoundingClientRect();
  const titleWidth = maxTitleWidth || titleEditorRef.current.clientWidth;
  const BUFFER_SPACE = 10; // Add some buffer space for safety
  const fitsToRight = titleWidth + menuBox.clientWidth + DOCUMENT_PADDING + BUTTON_WIDTH + BUFFER_SPACE < parentDocumentElem.clientWidth;
  const fitsBelow =
    titleWidth + menuBox.clientWidth + BUTTON_WIDTH + BUFFER_SPACE > parentDocumentElem.clientWidth && menuButtonBoundingBox.left - menuBox.clientWidth > parentDocumentElemBoundingBox.left;

  // If we have enough space for the title plus menu plus buffer, position to the left
  // Also check if we're scrolled horizontally
  if (!fitsToRight && !fitsBelow) {
    return "down-centered";
  } else if (fitsToRight) {
    return "left";
  } else if (fitsBelow) {
    return "down";
  }
};

const getTextareaStyles = (titleEditorRef, maxWidth) => {
  if (!titleEditorRef.current || !maxWidth) return;
  const minWidth = 50;
  const refValue = titleEditorRef.current;

  const text = refValue.value || refValue.placeholder || "";
  const textMetrics = getTextMetrics(titleEditorRef, text);
  const w = Math.min(Math.max(textMetrics.width + 4, minWidth), maxWidth);

  return {
    width: `${w}px`,
    whiteSpace: textMetrics.width < maxWidth ? "nowrap" : "pre-wrap",
  };
};

export const EditorContainerContext = createContext({
  height: { offset: 0, glyphHeight: undefined, isSingleLine: true },
  titleEditorRef: { current: null },
  textareaStyles: { width: "50px", whiteSpace: "initial" },
  maxTitleWidth: undefined,
  titleMaxWidthOffset: 0,
  getTextMetrics,
  getTextHeight,
  setMaxTitleWidth: () => {},
  menuPosition: undefined,
  setMenuPosition: () => {},
  title: "",
  setTitle: () => {},
});

export const EditorContainerProvider = ({ children, titleEditorRef, titleMaxWidthOffset = 0 }) => {
  const [glyphHeight, setGlyphHeight] = useState({ offset: 0, glyphHeight: undefined });
  const [maxTitleWidth, setMaxTitleWidth] = useState();
  const [menuPosition, setMenuPosition] = useState();
  const [textareaStyles, setTextareaStyles] = useState({ width: "50px", whiteSpace: "initial" });
  const [title, setTitle] = useState("");

  const handleSetMenuPosition = useMemo(() => {
    return () => {
      const newMaxWidth = getTitleMaxWidth(titleEditorRef, titleMaxWidthOffset);
      setMenuPosition(determineMenuPosition(titleEditorRef, newMaxWidth));
    };
  }, [titleEditorRef, titleMaxWidthOffset]);

  const handleSetGlyphHeight = useMemo(() => {
    return () => {
      const newMaxWidth = getTitleMaxWidth(titleEditorRef, titleMaxWidthOffset);

      setGlyphHeight(getTextHeight(titleEditorRef, title, newMaxWidth));
      setTextareaStyles(getTextareaStyles(titleEditorRef, newMaxWidth));
      setMenuPosition(determineMenuPosition(titleEditorRef, newMaxWidth));
      setMaxTitleWidth(newMaxWidth);
    };
  }, [titleEditorRef, title, titleMaxWidthOffset]);

  useEffect(() => {
    if (!titleEditorRef.current) return;

    let resizeTimeout;
    const adjustOnResize = () => {
      // Clear any pending resize timeout
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      // Batch all updates in a single timeout
      resizeTimeout = setTimeout(() => {
        const newMaxWidth = getTitleMaxWidth(titleEditorRef, titleMaxWidthOffset);
        if (!newMaxWidth) return;

        // Batch all state updates together
        const newTextareaStyles = getTextareaStyles(titleEditorRef, newMaxWidth);
        const newMenuPosition = determineMenuPosition(titleEditorRef, newMaxWidth);

        setTextareaStyles(newTextareaStyles);
        setMenuPosition(newMenuPosition);
        setMaxTitleWidth(newMaxWidth);
        handleSetGlyphHeight();
      }, 0); // debounce to avoid many consecutive updates in close succession
    };

    adjustOnResize();
    window.addEventListener("resize", adjustOnResize);

    // Create a ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver(adjustOnResize);
    const editorSection = titleEditorRef.current.closest("#editor-section");
    if (editorSection) {
      resizeObserver.observe(editorSection);
    }

    return () => {
      window.removeEventListener("resize", adjustOnResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      if (editorSection) {
        resizeObserver.unobserve(editorSection);
      }
      resizeObserver.disconnect();
    };
  }, [titleEditorRef, handleSetGlyphHeight, title, maxTitleWidth, titleMaxWidthOffset]);

  return (
    <EditorContainerContext.Provider
      value={{
        height: glyphHeight,
        titleEditorRef,
        maxTitleWidth,
        titleMaxWidthOffset,
        getTextMetrics,
        getTextHeight: handleSetGlyphHeight,
        setMaxTitleWidth,
        menuPosition,
        setMenuPosition: handleSetMenuPosition,
        textareaStyles,
        title,
        setTitle,
      }}
    >
      {children}
    </EditorContainerContext.Provider>
  );
};
